/**
 * Componente PeriodFilter
 * 
 * Filtro de período para o dashboard.
 * Permite selecionar diferentes períodos de tempo para visualização dos dados.
 * 
 * @module components/Dashboard/DashboardFilters/PeriodFilter
 */

import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FaCalendarAlt, FaChevronDown } from 'react-icons/fa';
import moment from 'moment';

/**
 * Opções de período disponíveis
 */
const PERIOD_OPTIONS = [
    { value: '7', label: 'Últimos 7 dias', days: 7 },
    { value: '30', label: 'Últimos 30 dias', days: 30 },
    { value: '90', label: 'Últimos 90 dias', days: 90 },
    { value: '365', label: 'Último ano', days: 365 },
];

/**
 * Formata as datas do período
 * 
 * @param {number} days - Número de dias
 * @returns {object} Objeto com data inicial e final formatadas
 */
const formatPeriodDates = (days) => {
    const endDate = moment();
    const startDate = moment().subtract(days, 'days');
    
    return {
        start: startDate.format('DD/MM/YYYY'),
        end: endDate.format('DD/MM/YYYY'),
        startLong: startDate.format('DD [de] MMMM [de] YYYY'),
        endLong: endDate.format('DD [de] MMMM [de] YYYY'),
    };
};

/**
 * Componente de filtro de período
 * 
 * @param {object} props - Propriedades do componente
 * @param {string} props.period - Período selecionado
 * @param {function} props.onPeriodChange - Callback quando período muda
 * @param {string} [props.className] - Classes CSS adicionais
 * 
 * @returns {JSX.Element} Componente de filtro de período
 */
const PeriodFilter = ({ period, onPeriodChange, className = '' }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);
    const buttonRef = useRef(null);
    
    const selectedOption = PERIOD_OPTIONS.find(opt => opt.value === period) || PERIOD_OPTIONS[1];
    const periodDates = formatPeriodDates(selectedOption.days);

    // Fechar dropdown ao clicar fora
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                dropdownRef.current && 
                buttonRef.current &&
                !dropdownRef.current.contains(event.target) &&
                !buttonRef.current.contains(event.target)
            ) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            // Fechar ao pressionar Escape
            const handleEscape = (event) => {
                if (event.key === 'Escape') {
                    setIsOpen(false);
                }
            };
            document.addEventListener('keydown', handleEscape);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
                document.removeEventListener('keydown', handleEscape);
            };
        }
    }, [isOpen]);

    const handleSelect = (value) => {
        onPeriodChange(value);
        setIsOpen(false);
    };

    return (
        <div className={`relative ${className}`} ref={dropdownRef}>
            <button
                ref={buttonRef}
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className="
                    group
                    flex items-center gap-2
                    px-3 sm:px-4 py-2.5
                    text-sm font-medium
                    text-gray-700
                    bg-white
                    border border-gray-300
                    rounded-lg
                    shadow-sm
                    hover:border-emerald-400
                    focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500
                    transition-all duration-200
                    cursor-pointer
                    min-w-[160px]
                "
                aria-label="Selecionar período"
                aria-expanded={isOpen}
                aria-haspopup="listbox"
            >
                {/* Ícone de calendário */}
                <FaCalendarAlt 
                    className="text-gray-400 group-hover:text-emerald-600 transition-colors duration-200" 
                    size={14}
                    aria-hidden="true"
                />
                
                {/* Texto do período selecionado */}
                <span className="flex-1 text-left">{selectedOption.label}</span>
                
                {/* Ícone de seta */}
                <FaChevronDown 
                    className={`text-gray-400 transition-transform duration-200 ${
                        isOpen ? 'transform rotate-180' : ''
                    }`}
                    size={12}
                    aria-hidden="true"
                />
            </button>

            {/* Dropdown Menu */}
            {isOpen && (
                <div 
                    className="
                        absolute top-full left-0 mt-1
                        w-full min-w-[280px]
                        bg-white
                        border border-gray-200
                        rounded-lg
                        shadow-lg
                        z-50
                        overflow-hidden
                    "
                    role="listbox"
                >
                    {PERIOD_OPTIONS.map((option) => {
                        const optionDates = formatPeriodDates(option.days);
                        const isSelected = option.value === period;
                        
                        return (
                            <button
                                key={option.value}
                                type="button"
                                onClick={() => handleSelect(option.value)}
                                className={`
                                    w-full
                                    px-4 py-3
                                    text-left
                                    transition-colors duration-150
                                    border-b border-gray-100 last:border-b-0
                                    hover:bg-emerald-50
                                    ${isSelected ? 'bg-emerald-50 border-l-4 border-l-emerald-500' : ''}
                                `}
                                role="option"
                                aria-selected={isSelected}
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <div className="flex-1">
                                        <div className={`
                                            text-sm font-medium mb-1
                                            ${isSelected ? 'text-emerald-700' : 'text-gray-900'}
                                        `}>
                                            {option.label}
                                        </div>
                                        <div className="text-xs text-gray-500 space-y-0.5">
                                            <div>{optionDates.start} → {optionDates.end}</div>
                                            <div className="text-gray-400">
                                                {optionDates.startLong} até {optionDates.endLong}
                                            </div>
                                        </div>
                                    </div>
                                    {isSelected && (
                                        <div className="flex-shrink-0 mt-0.5">
                                            <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                                        </div>
                                    )}
                                </div>
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

PeriodFilter.propTypes = {
    /** Período selecionado */
    period: PropTypes.string.isRequired,
    
    /** Callback quando período muda */
    onPeriodChange: PropTypes.func.isRequired,
    
    /** Classes CSS adicionais */
    className: PropTypes.string,
};

PeriodFilter.defaultProps = {
    className: '',
};

export default PeriodFilter;

